//lib
import ChartContext from "../../../context/ChartContext";
import { useParams } from "react-router-dom";
import GanttChartWrapper from "./gantt-chart-wrapper/gantt-chart-wrapper";
import React, { useState, useContext, useRef, useEffect, useMemo } from "react";

//src
import { db } from "../../../services/firebase";
import { COLOUR_CONSTANT, DAY_MILLISECONDS } from "../../../utils/contants";

const GanttChart = () => {
  const { id } = useParams();
  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const colors = useMemo(
    () => [
      "#FFDCAD",
      "#ADEE96",
      COLOUR_CONSTANT.theme.purple.dark,
      COLOUR_CONSTANT.theme.medium,
    ],
    []
  );

  const chartComponentRef = useRef(null);
  const { setExportGanttChart } = useContext(ChartContext);
  const hasData = series.some((task) => task.start && task.end);

  useEffect(() => {
    setExportGanttChart(() => () => {
      const chart = chartComponentRef.current?.chart;
      if (!isLoading && series.length > 0) {
        chart.exportChartLocal({ type: "application/pdf" });
      } else {
        alert("Please wait for the data to load before exporting");
      }
    });
  }, [isLoading, series, setExportGanttChart]);

  useEffect(() => {
    const unsubscribe = db
      .collection("projects")
      .doc(id)
      .onSnapshot(
        (doc) => {
          if (!doc.exists) {
            console.error("Document does not exist");
            setSeries([]);
            setIsLoading(false);
            return;
          }

          const projectData = doc.data();
          const { deliverables = [], plannerType } = projectData;

          if (deliverables.length === 0) {
            console.log("No deliverables found");
            setSeries([]);
            setIsLoading(false);
            return;
          }

          const isFixedDeliverable = plannerType === "Fixed Fee Deliverable";

          const newSeries = isFixedDeliverable
            ? // ---- only show milestones/deliverables ----
              deliverables.map((milestone, idx) => {
                // parse your dateRange (e.g. "Jun 18, 2024 - Jun 30, 2025")
                const [startStr, endStr] = milestone.dateRange.split(" - ");
                const start = Date.parse(startStr);
                const end = Date.parse(endStr);

                return {
                  id: `milestone-${idx}`,
                  name: milestone.name,
                  start,
                  end,
                  color: COLOUR_CONSTANT.theme.medium,
                };
              })
            : // ---- existing milestone + tasks logic ----
              deliverables.flatMap((milestone, idx) => {
                const color = colors[idx % colors.length];
                const milestoneColor = COLOUR_CONSTANT.theme.medium;

                const tasks = (milestone.tasks || []).map((task, tIdx) => {
                  let startDate = Date.parse(task.startDate);
                  let endDate = Date.parse(task.endDate);
                  if (startDate === endDate) {
                    endDate += DAY_MILLISECONDS;
                  }
                  return {
                    id: `task-${idx}-${tIdx}`,
                    parent: `milestone-${idx}`,
                    name: task.description,
                    start: startDate,
                    end: endDate,
                    completed: task.progress ? task.progress / 100 : 0,
                    color,
                  };
                });

                const milestoneStart = tasks[0]?.start ?? 0;
                const milestoneEnd = tasks[tasks.length - 1]?.end ?? 0;

                return [
                  {
                    id: `milestone-${idx}`,
                    name: milestone.name,
                    start: milestoneStart,
                    end: milestoneEnd,
                    color: milestoneColor,
                  },
                  ...tasks,
                ];
              });

          setSeries(newSeries);
          setIsLoading(false);
        },
        (error) => {
          console.error("Error listening to the document:", error);
          setIsLoading(false);
        }
      );

    return () => unsubscribe();
  }, [id, colors]);

  const NoDataOverlay = () => (
    <div
      style={{
        height: "400px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
      }}
    >
      <p className="headingXS">No data to show</p>
    </div>
  );

  const LoadingOverlay = () => (
    <div
      style={{
        height: "400px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
      }}
    >
      <p className="headingXS">Loading...</p>
    </div>
  );

  return (
    <div>
      {isLoading && <LoadingOverlay />}{" "}
      {/* Show loading overlay when loading */}
      {!isLoading && !hasData && <NoDataOverlay />}{" "}
      {/* Show no data overlay only when not loading and no data */}
      {hasData && (
        <>
          <GanttChartWrapper ref={chartComponentRef} series={series} />
        </>
      )}
    </div>
  );
};

export default GanttChart;
